import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    withStyles, Button, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Select, MenuItem, FormControl, InputLabel, Input
} from "@material-ui/core";
import CancelAcceptForm from "../../../../Common/CancelAcceptForm";
import Status from "../../../../Common/BulletStatus";
import CustomLabel from "../../../../Common/CustomLabel";
import t from "../../../../../languages/pl";
import { themeTypeToBool } from '../../../../../utils/helpers';
import { buttonStyle } from "../buttonGroupStyle";
import CustomSwitch from "../../../../Common/CustomSwitch";
import PhoneTextField from '../../../../Common/InputFields/PhoneTextField';
import PhoneInput from './../../../../Common/InputFields/PhoneInput';
import { mapDeviceSettingsDto } from './../../../../../requests/mapApiObject';

const useStyles = theme => ({
    button: buttonStyle(theme),
    container: {
        margin: theme.spacing(1)
    },
    label: {
        color: themeTypeToBool(theme.palette.type) ? "#000" : "#FFF",
    },
    textField: {
        marginRight: theme.spacing(2),
        minWidth: 200
    },
    formControl: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    pinSelect: {
        minWidth: 50,
    },
    row: {
        display: "flex",
        alignItems: "center"
    },
    switchesRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
    },
    singleSwitch: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    additionalInfo: {
        margin: theme.spacing(1)
    },
    pinField: {
        "&:focus": {
            background: "transparent"
        }
    },
    infoText: {
        textAlign: "center",
        color: 'red',
        marginBottom: theme.spacing(2)
    }
});

const pinOptions = () => {
    let options = [];
    for (let i = 1; i < 16; i++) {
        let value = i.toString();
        if (i < 10)
            value = "0" + value;
        options.push(<MenuItem key={i} value={value}>{(value)}</MenuItem>);
    }
    return options;
};

const DeviceSettings = props => {
    const { classes, name = "", data, onDialogOpen, onSubmit, dataNotSaved = false } = props;
    const [open, setOpen] = useState(false);
    const [pinPrefix, setPinPrefix] = useState('');
    const [pinSuffix, setPinSuffix] = useState('');
    const [state, setState] = useState(data);

    useEffect(() => {
        setState({
            ...data,
        });
        if (data.pin.length === 4) {
            setPinPrefix(data.pin.substring(0, 2));
            setPinSuffix(data.pin.substring(2, 4));
        }
    }, [data]);

    useEffect(() => {
        let pin = pinPrefix + pinSuffix;
        setState({
            ...state,
            pin: pin
        });
    }, [pinPrefix, pinSuffix]);


    const handleDialogOpen = () => {
        setOpen(true);
        onDialogOpen && onDialogOpen();
    };

    const handleSubmit = () => {
        setOpen(false);
        onSubmit && onSubmit(mapDeviceSettingsDto(state));
    };

    return (
        <React.Fragment>
            <Button
                variant="outlined"
                className={classes.button}
                onClick={handleDialogOpen}
            >
                {t.settings}
            </Button>

            <Dialog
                open={open}
                fullWidth={true}
                maxWidth="lg"
                onClose={() => setOpen(false)}
            >
                <DialogTitle>
                    {t.carDeviceSettings}&nbsp;{name}
                </DialogTitle>
                <DialogContent>
                    <div className={classes.container}>
                    {
                        dataNotSaved &&
                        <Typography className={classes.infoText} variant="body2">
                            {t.deviceDetailsAdditionalInfo1}<br />{t.deviceDetailsAdditionalInfo2}
                        </Typography>
                    }
                        <div className={classes.formControl}>
                            <PhoneTextField
                                label={t.devicePhoneNo}
                                value={state.telefon_urzadzenia}
                                onChange={(value) => setState({ ...state, telefon_urzadzenia: value })}
                                className={classes.textField}
                                InputLabelProps={{
                                    shrink: true,
                                    className: classes.label
                                }}
                            />
                            <TextField
                                label={t.passwordForSMS}
                                value={state.haslo}
                                onChange={(e) => setState({ ...state, haslo: e.target.value })}
                                className={classes.textField}
                                InputLabelProps={{
                                    shrink: true,
                                    className: classes.label
                                }}
                            />
                        </div>
                        <div className={classes.row}>
                            <FormControl className={classes.formControl}>
                                <InputLabel
                                    shrink={true}
                                    className={classes.label}
                                >
                                    {t.pinCode}
                                </InputLabel>
                                <Select
                                    className={classes.pinSelect}
                                    classes={{
                                        select: classes.pinField
                                    }}
                                    value={pinPrefix}
                                    onChange={(e) => setPinPrefix(e.target.value)}
                                >
                                    {pinOptions()}
                                </Select>
                            </FormControl>
                            <FormControl className={classes.formControl}>
                                <InputLabel
                                    shrink={true}
                                    className={classes.label}
                                >
                                    &nbsp;
                                </InputLabel>
                                <Select
                                    className={classes.pinSelect}
                                    classes={{
                                        select: classes.pinField
                                    }}
                                    value={pinSuffix}
                                    onChange={(e) => setPinSuffix(e.target.value)}
                                >
                                    {pinOptions()}
                                </Select>
                            </FormControl>
                        </div>
                        <div className={classes.row}>
                            <FormControl className={classes.formControl}>
                                <CustomLabel>{t.possibleMaxSpeed}</CustomLabel>
                                <Input
                                    color="secondary"
                                    value={state.predkosc_max}
                                    onChange={(e) => setState({ ...state, predkosc_max: e.target.value })}
                                    className={classes.textField}
                                />
                            </FormControl>
                            {
                                state.monitorowanie
                                ?
                                <Status background="#69E4A6">
                                    {t.monitoringActive}
                                </Status>
                                :
                                <Status background="#FC9696">
                                    {t.monitoringNotActive}
                                </Status>
                            }
                        </div>
                        <div className={classes.switchesRow}>
                            <div className={classes.singleSwitch}>
                                <FormControl className={classes.formControl}>
                                    <CustomLabel>{t.phoneNumberForNotifications}</CustomLabel>
                                    <PhoneInput
                                        value={state.telefon_alarmowy1.numer}
                                        onChange={(newValue) => setState({ ...state, telefon_alarmowy1: { ...state.telefon_alarmowy1, numer: newValue } })}
                                        className={classes.textField}
                                    />
                                </FormControl>
                            </div>
                            <div className={classes.singleSwitch}>
                            <Typography variant="subtitle2">{t.doesPhoneNo1Active}</Typography>
                            <CustomSwitch
                                checked={state.telefon_alarmowy1.aktywny}
                                onChange={(e) => setState({ ...state, telefon_alarmowy1: { ...state.telefon_alarmowy1, aktywny: e.target.checked } })}
                            />
                            </div>
                            <div className={classes.singleSwitch}>
                                <Typography variant="subtitle2">{t.detectionBatteryStatus}</Typography>
                                <CustomSwitch
                                    checked={state.telefon_alarmowy1.stan_akumulatora}
                                    onChange={(e) => setState({ ...state, telefon_alarmowy1: { ...state.telefon_alarmowy1, stan_akumulatora: e.target.checked }})}
                                />
                            </div>
                            <div className={classes.singleSwitch}>
                                <Typography variant="subtitle2">{t.detectionCarBreakIn}</Typography>
                                <CustomSwitch
                                    checked={state.telefon_alarmowy1.wlamanie}
                                    onChange={(e) => setState({ ...state, telefon_alarmowy1: { ...state.telefon_alarmowy1, wlamanie: e.target.checked }})}
                                />
                            </div>
                        </div>
                        <div className={classes.switchesRow}>
                            <div className={classes.singleSwitch}>
                                <FormControl className={classes.formControl}>
                                    <CustomLabel>{t.phoneNumberForNotifications}</CustomLabel>
                                    <PhoneInput
                                        value={state.telefon_alarmowy2.numer}
                                        onChange={(newValue) => setState({ ...state, telefon_alarmowy2: { ...state.telefon_alarmowy2, numer: newValue } })}
                                        className={classes.textField}
                                    />
                                </FormControl>
                            </div>
                            <div className={classes.singleSwitch}>
                                <Typography variant="subtitle2">{t.doesPhoneNo2Active}</Typography>
                                &nbsp;
                                <CustomSwitch
                                    checked={state.telefon_alarmowy2.aktywny}
                                    onChange={(e) => setState({ ...state, telefon_alarmowy2: { ...state.telefon_alarmowy2, aktywny: e.target.checked } })}
                                />
                            </div>
                            <div className={classes.singleSwitch}>
                                <Typography variant="subtitle2">{t.detectionBatteryStatus}</Typography>
                                <CustomSwitch
                                    checked={state.telefon_alarmowy2.stan_akumulatora}
                                    onChange={(e) => setState({ ...state, telefon_alarmowy2: { ...state.telefon_alarmowy2, stan_akumulatora: e.target.checked }})}
                                />
                            </div>
                            <div className={classes.singleSwitch}>
                                <Typography variant="subtitle2">{t.detectionCarBreakIn}</Typography>
                                <CustomSwitch
                                    checked={state.telefon_alarmowy2.wlamanie}
                                    onChange={(e) => setState({ ...state, telefon_alarmowy2: { ...state.telefon_alarmowy2, wlamanie: e.target.checked }})}
                                />
                            </div>
                        </div>
                        <div className={classes.additionalInfo}>
                            <Typography
                                variant="body2"
                            >
                                {t.deviceSettingsDialogInfoText1}
                            </Typography>
                            <ul style={{ margin: 0 }} >
                                <li>{t.deviceSettingsDialogInfoText2}</li>
                                <li>{t.deviceSettingsDialogInfoText3}</li>
                                <li>{t.deviceSettingsDialogInfoText4}</li>
                            </ul>
                            <Typography
                                variant="body2"
                            >
                                {t.deviceSettingsDialogInfoText5}
                            </Typography>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <CancelAcceptForm onSubmit={handleSubmit} onCancel={() => setOpen(false)} />
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

export default withStyles(useStyles)(DeviceSettings);

DeviceSettings.propTypes = {
    classes: PropTypes.object,
    name: PropTypes.string,
    data: PropTypes.object,
    onDialogOpen: PropTypes.func,
    onSubmit: PropTypes.func,
    dataNotSaved: PropTypes.bool
};